import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useAppState } from '../../../AppStateProvider';
import type { EmailLengthText, GenerateEmailQuery, Reaction } from '../../../domain/generatedEmail';
import type { Insight } from '../../../domain/insight';
import { InsightType } from '../../../domain/insightType';
import { USER_SAM_EMAIL_LENGTH_TEXT } from '../../../domain/user';
import { useCheckIfStaleOpportunity } from '../../../features/competitors/useCheckIfStaleOpportunity';
import { useCompetitorsQuery } from '../../../features/competitors/useCompetitorsQuery';
import { insightsApi } from '../../../features/insights/api';
import { useIsOutOfEmailsQuota } from '../../../features/insights/useIsOutOfEmailsQuota';
import { useUserSettings } from '../../../features/user/useUserSettings';
import { usePrevious } from '../../../hooks/usePrevious';
import { appStore } from '../../../services/appStore';
import { copyInsight } from '../../../services/clipboard/copyInsight';
import InsightsTextService from '../../../services/insightsTextService';
import { useDispatchTrackEventWithLocation } from '../../../services/tracking/hooks/useDispatchTrackEventWithLocation';
import { InsightsQuotaTooltip } from '../InsightsQuotaTooltip';

import { CompetitorsModal } from './CompetitorsModal';
import { EmailPreviewFooter } from './EmailPreviewFooter';
import { EmailPreviewLoading } from './EmailPreviewLoading';
import { InsightGraph } from './InsightGraph';
import {
  StyledChangeCompetitors,
  StyledEmailCard,
  StyledEmailCardHeader,
  StyledEmailIcon,
  StyledEmailInsight,
  StyledEmailSection,
  StyledEmailText,
  StyledSeparator,
} from './styles';

export type EmailPreviewProps = {
  queries: GenerateEmailQuery[];
  insight: Insight;
  positionInList: string;
  onRegenerate(insightId: string, lengthParam: EmailLengthText): void;
  isFirstBatch: boolean;
};

export const EmailPreview = ({ queries, insight, onRegenerate, positionInList, isFirstBatch }: EmailPreviewProps) => {
  const { t } = useTranslation();
  const {
    emailGenerationOptions: { emailReactions, regeneratedOpportunitiesIds },
  } = useAppState();

  const { getUserSetting, setUserSettings } = useUserSettings();
  const dispatchTrackEventWithLocation = useDispatchTrackEventWithLocation();
  const competitors = useCompetitorsQuery().data ?? [];
  const [emailIndex, setEmailIndex] = useState(0);
  const [isCompetitorsModalOpen, setIsCompetitorsModalOpen] = useState(false);
  const prevQueries = usePrevious(queries);
  const graphRef = useRef<HTMLDivElement>(null);
  const isStale = useCheckIfStaleOpportunity()(insight.id);
  const isOutOfEmailsQuota = useIsOutOfEmailsQuota();

  const { email, isLoading, requestId } = queries[emailIndex] || {};
  const content = `${email?.insights || ''}\n\n${email?.businessAffect || ''}`;

  const isOpportunity = insight.insightType === InsightType.Opportunity;

  useEffect(() => {
    if (prevQueries?.length !== queries.length) {
      setEmailIndex(queries.length - 1);
    }
  }, [queries]);

  const handleEmailIndexChange = (index: number) => {
    setEmailIndex(index);
  };

  const handleCopy = () => {
    const graphElement = graphRef.current;
    if (!graphElement) {
      return;
    }

    copyInsight(graphElement, content)
      .then(() => {
        appStore.setToast({ content: t('insights.email.copied'), icon: 'checked' });
        dispatchTrackEventWithLocation(
          'ai outreach',
          'copy ai talk point',
          `${InsightsTextService.getInsightText(insight)}/${positionInList}/${emailIndex + 1}/${queries.length}`
        );
        if (requestId) {
          insightsApi.trackCopy(requestId).catch(console.error);
        }
      })
      .catch(console.error);
  };

  const handleRegenerate = (lengthParam: EmailLengthText) => {
    onRegenerate(insight.id, lengthParam);
    dispatchTrackEventWithLocation(
      'ai outreach',
      'regenerate',
      `regenerate talk point/${positionInList}/${queries.length}`
    );

    if (lengthParam !== getUserSetting(USER_SAM_EMAIL_LENGTH_TEXT)) {
      setUserSettings({ [USER_SAM_EMAIL_LENGTH_TEXT]: lengthParam });
    }
  };

  const handleFeedback = (reaction: Reaction) => {
    if (!requestId || emailReactions[requestId] === reaction) {
      return;
    }

    dispatchTrackEventWithLocation(
      'ai outreach',
      `${reaction} output`,
      `${InsightsTextService.getInsightText(insight)}/${positionInList}/${emailIndex + 1}/${queries.length}`
    );
    insightsApi.trackLike(requestId, reaction).catch(console.error);
    appStore.setEmailReaction(requestId, reaction);
  };

  const handleChangeCompetitors = () => {
    setIsCompetitorsModalOpen(true);
    dispatchTrackEventWithLocation(
      'ai outreach',
      'edit competitors',
      `${InsightsTextService.getInsightText(insight)}/${competitors.length}`
    );
  };

  if (!isLoading && !email) {
    return null;
  }

  const footer = (
    <EmailPreviewFooter
      queries={queries}
      emailIndex={emailIndex}
      onEmailIndexChange={handleEmailIndexChange}
      onCopy={handleCopy}
      onFeedback={handleFeedback}
      onRegenerate={handleRegenerate}
      isStale={isStale}
    />
  );

  const changeCompetitorsButton = (
    <StyledChangeCompetitors
      onClick={handleChangeCompetitors}
      disabled={isOutOfEmailsQuota}
      data-automation="ChangeCompetitors"
    >
      {t('insights.competitors.change')}
    </StyledChangeCompetitors>
  );

  if (isLoading) {
    const shouldAnimate = !isFirstBatch && queries.length === 1 && !regeneratedOpportunitiesIds.includes(insight.id);
    return <EmailPreviewLoading footer={footer} shouldAnimate={shouldAnimate} insight={insight} />;
  }

  return (
    <>
      <StyledEmailCard data-automation="EmailCard">
        <StyledEmailCardHeader>
          <StyledEmailIcon iconName="sparkles-gradient" size="xs" />
          <StyledEmailInsight data-automation="EmailInsight">
            {t('insights.email.based_on')}&nbsp;{InsightsTextService.getInsightText(insight)}
          </StyledEmailInsight>
        </StyledEmailCardHeader>
        <StyledEmailSection>
          <StyledEmailText data-automation="EmailText">{content}</StyledEmailText>
          <InsightGraph insight={insight} ref={graphRef} />
          {isOpportunity ? (
            <>
              {isOutOfEmailsQuota ? (
                <InsightsQuotaTooltip location="competitors" maxWidth={230} minWidth={230} placement="top-right">
                  {changeCompetitorsButton}
                </InsightsQuotaTooltip>
              ) : (
                changeCompetitorsButton
              )}
              <StyledSeparator />
            </>
          ) : null}
          {footer}
        </StyledEmailSection>
      </StyledEmailCard>
      {isOpportunity ? (
        <CompetitorsModal
          isOpen={isCompetitorsModalOpen}
          onClose={() => setIsCompetitorsModalOpen(false)}
          opportunity={insight}
        />
      ) : null}
    </>
  );
};
